import { Popover } from 'antd';
import {
  ActivityCommentIcon,
  ArrowForwardIcon,
  BellIcon,
  MuteBellIcon,
  PeopleAltIcon,
} from '@/assets/icons/icons';
import { useToken } from '@/common/helper';
import { getFormattedTime } from '@/common/utils/getFormattedTimeUtils';
import type {
  ShipmentActivity,
  ShipmentActivityBell,
  ShipmentActivityPreviewItem,
  ShipmentRow,
} from '../../types';
import { NOTIFICATION_MUTED } from './shipmentTableConstants';
import { textOrEmpty } from './shipmentTableUtils';

interface ShipmentActivityCellProps {
  shipment: ShipmentRow;
  onOpenComments?: (
    commentId?: string | null,
    meta?: { mentionsMe?: boolean; scrollToCommentsBottom?: boolean }
  ) => void;
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const toNumber = (value: unknown): number => {
  const next = Number(value);
  return Number.isFinite(next) ? next : 0;
};

const toBoolean = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  return false;
};

const normalizeActivityBell = (value: unknown): ShipmentActivityBell => {
  if (typeof value !== 'string') return null;
  const normalized = value.toLowerCase();
  if (normalized === 'on') return 'on';
  if (normalized === NOTIFICATION_MUTED) return 'muted';
  return null;
};

const normalizePreviewItems = (
  value: unknown
): ShipmentActivityPreviewItem[] =>
  Array.isArray(value) ? (value as ShipmentActivityPreviewItem[]) : [];

const getShipmentActivity = (record: ShipmentRow): ShipmentActivity => {
  const activity = asRecord(record.activity);

  return {
    comment_count: toNumber(activity?.comment_count),
    has_unread: toBoolean(activity?.has_unread),
    has_unread_mention: toBoolean(activity?.has_unread_mention),
    unread_count: toNumber(activity?.unread_count),
    mention_unread_count: toNumber(activity?.mention_unread_count),
    preview: normalizePreviewItems(activity?.preview),
    bell: normalizeActivityBell(activity?.bell),
  };
};

const stripHtml = (value: unknown): string =>
  String(value ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const getPreviewCommentId = (
  item: ShipmentActivityPreviewItem
): string | null => {
  const id = item.id;
  return id == null ? null : String(id);
};

const getPreviewAuthor = (item: ShipmentActivityPreviewItem): string =>
  textOrEmpty(item.author_name || item.author || 'Unknown');

const getPreviewText = (item: ShipmentActivityPreviewItem): string =>
  stripHtml(item.text);

const getPreviewTime = (
  item: ShipmentActivityPreviewItem,
  isTimeZone?: boolean
): string =>
  getFormattedTime(textOrEmpty(item.timestamp), isTimeZone)?.relative || '--';

const renderPreviewText = (text: string) => {
  const parts = text.split(/(@[A-Za-z0-9_.-]+)/g);
  return parts.map((part, index) =>
    part.startsWith('@') ? (
      <span
        className='shipment-activity-preview-mention'
        key={`${part}-${index}`}
      >
        {part}
      </span>
    ) : (
      part
    )
  );
};

const ShipmentActivityPreview = ({
  activity,
  onOpenComments,
}: {
  activity: ShipmentActivity;
  onOpenComments?: ShipmentActivityCellProps['onOpenComments'];
}) => {
  const visiblePreview = activity.preview.slice(0, 3);
  const { decodedUserToken } = useToken();
  const isTimeZone = decodedUserToken()?.time_zone_enable;

  return (
    <div className='shipment-activity-preview-card'>
      <div className='shipment-activity-preview-list'>
        {visiblePreview.map((item, index) => {
          const commentId = getPreviewCommentId(item);
          const commentText = getPreviewText(item);
          const mentionsViewer = toBoolean(item.mentions_me);

          return (
            <button
              type='button'
              key={commentId || `${getPreviewAuthor(item)}-${index}`}
              className='shipment-activity-preview-item'
              onClick={(event) => {
                event.stopPropagation();
                onOpenComments?.(commentId, { mentionsMe: mentionsViewer });
              }}
            >
              <div className='shipment-activity-preview-meta'>
                <span className='shipment-activity-preview-author'>
                  {getPreviewAuthor(item)}
                </span>
                <span className='shipment-activity-preview-audience'>
                  <PeopleAltIcon aria-hidden='true' />
                </span>
                <span className='shipment-activity-preview-time'>
                  {getPreviewTime(item, isTimeZone)}
                </span>
              </div>
              <div className='shipment-activity-preview-text'>
                {renderPreviewText(commentText || 'Comment')}
              </div>
            </button>
          );
        })}
      </div>
      <button
        type='button'
        className='shipment-activity-preview-footer'
        onClick={(event) => {
          event.stopPropagation();
          onOpenComments?.(null, { scrollToCommentsBottom: true });
        }}
      >
        <span>{activity.comment_count} total &middot;</span>
        <strong>See all comments</strong>
        <ArrowForwardIcon size={14} color='var(--text-primary)' />
      </button>
    </div>
  );
};

const ShipmentActivityCommentButton = ({
  activity,
  onOpenComments,
}: {
  activity: ShipmentActivity;
  onOpenComments?: ShipmentActivityCellProps['onOpenComments'];
}) => {
  if (activity.comment_count < 1) return null;

  const label = `${activity.comment_count} ${
    activity.comment_count === 1 ? 'comment' : 'comments'
  }${activity.has_unread ? ', unread' : ', read'}${
    activity.has_unread_mention ? ', mentions you' : ''
  }`;

  const button = (
    <button
      type='button'
      className={`shipment-activity-comment-button${
        activity.has_unread ? ' is-unread' : ''
      }`}
      aria-label={label}
      onClick={(event) => {
        event.stopPropagation();
        onOpenComments?.(null);
      }}
    >
      <span className='shipment-activity-comment-icon-wrap'>
        {activity.has_unread ? (
          <ActivityCommentIcon filled color='var(--light-blue)' />
        ) : (
          <ActivityCommentIcon filled color='#A7A7A7' />
        )}
        {activity.has_unread_mention ? (
          <span className='shipment-activity-mention-indicator' />
        ) : null}
      </span>
      <span className='shipment-activity-comment-count'>
        {activity.comment_count}
      </span>
    </button>
  );

  if (activity.preview.length === 0) return button;

  return (
    <Popover
      arrow={false}
      placement='topRight'
      trigger={['hover', 'focus']}
      overlayClassName='shipment-activity-preview-popover'
      content={
        <ShipmentActivityPreview
          activity={activity}
          onOpenComments={onOpenComments}
        />
      }
    >
      {button}
    </Popover>
  );
};

const ShipmentActivityBellButton = ({
  bell,
}: {
  bell: ShipmentActivityBell;
}) => {
  if (!bell) return null;

  const isNotificationMuted = bell === 'muted';

  return (
    <span
      className='shipment-activity-bell-button'
      aria-label={
        isNotificationMuted
          ? 'Shipment notifications muted'
          : 'Shipment notifications on'
      }
      onPointerDown={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      {isNotificationMuted ? (
        <MuteBellIcon color='var(--light-blue)' width={16} height={16} />
      ) : (
        <BellIcon color='var(--light-blue)' width={14} height={16} />
      )}
    </span>
  );
};

const ShipmentActivityCell = ({
  shipment,
  onOpenComments,
}: ShipmentActivityCellProps) => {
  const activity = getShipmentActivity(shipment);
  const hasComments = activity.comment_count >= 1;
  const hasBell = Boolean(activity.bell);

  if (!hasComments && !hasBell) return null;

  return (
    <span
      className={`shipment-activity-cell${
        !hasBell ? ' has-no-notification-bell' : ''
      }`}
    >
      <span className='shipment-activity-comment-slot'>
        {hasComments ? (
          <ShipmentActivityCommentButton
            activity={activity}
            onOpenComments={onOpenComments}
          />
        ) : null}
      </span>
      <span className='shipment-activity-bell-slot'>
        <ShipmentActivityBellButton bell={activity.bell} />
      </span>
    </span>
  );
};

export default ShipmentActivityCell;
